import { type AiBuilderUsageInfo } from '../stores/types';

/** 같은 세션에서 동시에 진행할 수 없는 AI 작업. 서버는 턴 lock 으로 같은 규칙을 강제한다. */
export type AiBuilderActiveOperation = 'chat' | 'generator';

/**
 * 채팅과 생성기가 공유하는 서버 세션 접근점. 세션 생성·복원·사용량 소진 UI 는 아직
 * 채팅 Provider 가 소유하므로, 채팅 Provider 가 자신의 구현을 여기에 등록한다.
 */
export type AiBuilderSessionPort = {
  draftInstanceId: string;
  getSessionId: () => string | null;
  getUsage: () => AiBuilderUsageInfo | null;
  /** 세션이 없으면 만든다. 복원 대기 중이거나 도중에 취소되면 null. */
  ensureSession: () => Promise<string | null>;
};

type Listener = () => void;

export type AiBuilderSessionStore = {
  subscribe: (listener: Listener) => () => void;
  getActiveOperation: () => AiBuilderActiveOperation | null;
  /** 다른 작업이 진행 중이면 false. 같은 작업을 다시 시작하는 것은 허용한다. */
  tryBeginOperation: (operation: AiBuilderActiveOperation) => boolean;
  /** 지금 진행 중인 작업이 operation 일 때만 비운다 — 다른 작업 표시를 지우지 않는다. */
  endOperation: (operation: AiBuilderActiveOperation) => void;
  registerPort: (port: AiBuilderSessionPort) => () => void;
  getPort: () => AiBuilderSessionPort | null;
};

export const createAiBuilderSessionStore = (): AiBuilderSessionStore => {
  let activeOperation: AiBuilderActiveOperation | null = null;
  let port: AiBuilderSessionPort | null = null;
  const listeners = new Set<Listener>();

  const setActiveOperation = (next: AiBuilderActiveOperation | null) => {
    if (activeOperation === next) return;
    activeOperation = next;
    listeners.forEach(listener => listener());
  };

  return {
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getActiveOperation: () => activeOperation,
    tryBeginOperation: operation => {
      if (activeOperation !== null && activeOperation !== operation) {
        return false;
      }
      setActiveOperation(operation);
      return true;
    },
    endOperation: operation => {
      if (activeOperation === operation) setActiveOperation(null);
    },
    registerPort: nextPort => {
      port = nextPort;
      return () => {
        if (port === nextPort) port = null;
      };
    },
    getPort: () => port,
  };
};
